/**
 * Deep links from a workspace's "How to unlock" hints (static/config/capability_unlocks.json).
 *
 * The cockpit opens `/settings/provider-keys?provider=<id>`, `/settings?settings=connections&connection=<id>`
 * or `/billing/checkout?offer=<id>`, each optionally with `return_to=<workspace URL>`. This module reads
 * those parameters defensively: an id is a short lower-case token (never free text), and a return path is
 * honoured only when it is an https address on the ingress host of one of the signed-in organization's own
 * workspaces (`GET /v1/workspaces`), so the link can never become an open redirect.
 */

const ID = /^[a-z0-9_]{1,64}$/;
const OFFER = /^[A-Z0-9][A-Z0-9-]{0,63}$/;

function param(search: string, name: string): string {
  try {
    const value = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search).getAll(name);
    return value.length === 1 ? value[0] : '';
  } catch {
    return '';
  }
}

/** The `provider` / `connection` id a hint linked to, or '' when absent or malformed. */
export function deepLinkId(search: string, name: 'provider' | 'connection'): string {
  const value = param(search, name);
  return ID.test(value) ? value : '';
}

/** The catalog offer id a hint linked to (`PROD-FILM`), or '' when absent or malformed. */
export function deepLinkOffer(search: string): string {
  const value = param(search, 'offer');
  return OFFER.test(value) ? value : '';
}

/** The raw `return_to` value, unvalidated (validate with {@link safeReturnTo}). */
export function returnToParam(search: string): string {
  return param(search, 'return_to');
}

/**
 * `raw` when it is `https://<ingress host of one of these workspaces>/<path>` with no credentials, port,
 * query or fragment, and the host is that workspace's own `wsp-<hex>.` label; otherwise ''.
 */
export function safeReturnTo(raw: string, workspaces: ReadonlyArray<{ workspace_id: string; ingress_host: string }>): string {
  if (!raw) return '';
  let url: URL;
  try {
    url = new URL(raw);
  } catch {
    return '';
  }
  if (url.protocol !== 'https:' || url.username || url.password || url.port || url.search || url.hash) return '';
  const host = url.hostname.toLowerCase();
  const owned = workspaces.some((workspace) => {
    const ingress = String(workspace.ingress_host || '').toLowerCase();
    const label = `wsp-${String(workspace.workspace_id || '').slice('wsp_'.length)}.`;
    return ingress === host && label.length > 5 && ingress.startsWith(label) && ingress.length > label.length;
  });
  return owned ? `https://${host}${url.pathname}` : '';
}
